import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Footer from './Footer';
import Header from './Header';

const SchoolListView = ({ onSupportClick }) => {
  const { schoolType } = useParams();
  const navigate = useNavigate();
  
  const [primaryZones, setPrimaryZones] = useState(null);
  const [secondaryZones, setSecondaryZones] = useState(null);
  const [selectedSchoolType, setSelectedSchoolType] = useState("Secondary");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'Rank', direction: 'ascending' });
  const [educationSector, setEducationSector] = useState('Government');
  const [viewMode, setViewMode] = useState('compact'); // 'compact' or 'full'
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  // Update URL when school type changes
  useEffect(() => {
    navigate(`/schools/${selectedSchoolType.toLowerCase()}`);
  }, [selectedSchoolType, navigate]);
  
  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      // Automatically set view mode based on screen size
      if (window.innerWidth < 768) {
        setViewMode('compact');
      } else {
        setViewMode('full');
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial value
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load school data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        // Load Primary Schools GeoJSON
        const primaryResponse = await fetch('/primary_rank_full.csv');
        const primaryText = await primaryResponse.text();
        const primaryData = {
          features: primaryText.split('\n').slice(1).map(line => {
            const [SchoolID,Score,School_Name,Rank,match_name,Suburb,Education_Sector,median_house_price,median_unit_price
            ] = line.split(',');
            return {
              properties: {
                Rank: Rank,
                School_Name: School_Name,
                Suburb: Suburb,
                Naplan_Score: Score,
                Education_Sector: Education_Sector,
                Selective_School: "N/A",
                median_house_price: median_house_price,
                median_unit_price: median_unit_price,
              }
            };
          }).filter(item => item.properties.School_Name) // Filter out empty rows
        };
        
        setPrimaryZones(primaryData);
  
        // Load Secondary Schools GeoJSON
        const secondaryResponse = await fetch('/school_rank_full.csv');
        const secondaryText = await secondaryResponse.text();
        const secondaryData = {
          features: secondaryText.split('\n').slice(1).map(line => {
            const [School_Name,Suburb,Address_Postcode,Education_Sector,School_Type,Selective_School,VCE_Score,Percentile,Rank,match_name,median_house_price,median_unit_price
            ] = line.split(',');
            return {
              properties: {
                Rank: Rank,
                School_Name: School_Name,
                Suburb: Suburb,
                VCE_Score: VCE_Score,
                Education_Sector: Education_Sector,
                Selective_School: Selective_School,
                median_house_price: median_house_price,
                median_unit_price: median_unit_price,
              }
            };
          }).filter(item => item.properties.School_Name) // Filter out empty rows
        };
        setSecondaryZones(secondaryData);
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        setError(`Failed to load data: ${error.message}`);
        setLoading(false);
      }
    }
    loadData();
  }, []);
  
  // Format currency values
  const formatPrice = (price) => {
    if (!price || price === 0) return "N/A";
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0
    }).format(price);
  };
  
  // Request sort based on column
  const requestSort = (key) => {
    // For primary schools, map VCE_Score sorting to Naplan
    const mappedKey = selectedSchoolType === "Primary" && key === "VCE_Score" ? "Naplan" : key;
    
    let direction = 'ascending';
    if (sortConfig.key === mappedKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: mappedKey, direction });
  };
  
  // Get sorted and filtered schools
  const sortedSchools = useMemo(() => {
    if (!primaryZones || !secondaryZones) return [];
    
    // Select the correct dataset based on school type
    const schoolData = selectedSchoolType === "Primary" 
      ? primaryZones.features 
      : secondaryZones.features;
    
    // Create a copy we can sort
    let sortableItems = [...schoolData];
    
    // Filter by search term if it exists
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      sortableItems = sortableItems.filter(school => 
        school.properties.School_Name.toLowerCase().includes(term) || 
        (school.properties.Suburb && school.properties.Suburb.toLowerCase().includes(term))
      );
    }
    
    // Filter by education sector if not "All"
    if (educationSector !== 'All') {
      sortableItems = sortableItems.filter(school => 
        school.properties.Education_Sector === educationSector
      );
    }
    
    // Sort the filtered items
    sortableItems.sort((a, b) => {
      let key = sortConfig.key;
      
      // Map the key for scoring based on school type
      if (key === "Naplan" && selectedSchoolType === "Secondary") {
        key = "VCE_Score";
      } else if (key === "VCE_Score" && selectedSchoolType === "Primary") {
        key = "Naplan";
      }
      
      // Handle special case for Rank sorting to put N/A at the bottom
      if (key === 'Rank') {
        const aRank = a.properties[key];
        const bRank = b.properties[key];
        
        // Check if either value is 'N/A'
        const aIsNA = aRank === 'N/A' || aRank === null || aRank === undefined;
        const bIsNA = bRank === 'N/A' || bRank === null || bRank === undefined;
        
        // If both are N/A, sort by school name
        if (aIsNA && bIsNA) {
          const aName = a.properties.School_Name || '';
          const bName = b.properties.School_Name || '';
          return aName.localeCompare(bName);
        }
        
        // If only one is N/A, put it at the bottom
        if (aIsNA) return 1;
        if (bIsNA) return -1;
        
        // If both have numeric values, compare as numbers
        const aValue = parseInt(aRank);
        const bValue = parseInt(bRank);
        
        // If ranks are equal, sort by house price ascending
        if (aValue === bValue) {
          const aHousePrice = parseFloat(a.properties.median_house_price) || 0;
          const bHousePrice = parseFloat(b.properties.median_house_price) || 0;
          return aHousePrice - bHousePrice;
        }
        
        if (sortConfig.direction === 'ascending') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
      
      // For other fields, handle numeric and text comparisons
      let aValue, bValue;
      
      // Special handling for numeric values
      if (key === 'VCE_Score' || key === 'Naplan' || key === 'median_house_price' || key === 'median_unit_price') {
        // Handle N/A values for numeric fields
        const aVal = a.properties[key];
        const bVal = b.properties[key];
        
        const aIsNA = aVal === 'N/A' || aVal === null || aVal === undefined || aVal === '';
        const bIsNA = bVal === 'N/A' || bVal === null || bVal === undefined || bVal === '';
        
        // If both are N/A, sort by school name
        if (aIsNA && bIsNA) {
          const aName = a.properties.School_Name || '';
          const bName = b.properties.School_Name || '';
          return aName.localeCompare(bName);
        }
        
        // If only one is N/A, put it at the bottom
        if (aIsNA) return 1;
        if (bIsNA) return -1;
        
        // If both have values, compare as numbers
        aValue = parseFloat(aVal) || 0;
        bValue = parseFloat(bVal) || 0;
      } else {
        // Text comparison
        aValue = a.properties[key] || '';
        bValue = b.properties[key] || '';
      }
      
      // Determine sort order
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      
      // If values are equal, use house price as the secondary sort (always ascending)
      const aHousePrice = parseFloat(a.properties.median_house_price) || 0;
      const bHousePrice = parseFloat(b.properties.median_house_price) || 0;
      return aHousePrice - bHousePrice;
    });
    
    return sortableItems;
  }, [primaryZones, secondaryZones, selectedSchoolType, searchTerm, sortConfig, educationSector]);
  
  // Render sort indicator
  const getSortIndicator = (columnName) => {
    // Adjust column name for scoring comparison based on school type
    let compareKey = columnName;
    if (columnName === "VCE_Score" && selectedSchoolType === "Primary") {
      compareKey = "Naplan";
    } else if (columnName === "Naplan" && selectedSchoolType === "Secondary") {
      compareKey = "VCE_Score";
    }
    
    if (sortConfig.key === compareKey) {
      return sortConfig.direction === 'ascending' ? '↑' : '↓';
    }
    return '';
  };
  
  // Determine which text color to use based on rank
  const getRankColorClass = (rank) => {
    const rankNum = parseInt(rank);
    if (isNaN(rankNum)) return "text-gray-700";
    
    if (rankNum <= 50) return "text-green-600 font-bold";
    if (rankNum <= 150) return "text-blue-600 font-bold";
    return "text-gray-700";
  };

  // Expanded card view for school
  const SchoolCard = ({ school }) => (
    <div className="bg-white p-4 rounded-lg shadow-md mb-4 border-l-4 border-blue-500">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-bold text-lg">
            {school.properties.School_Name} 
            {school.properties.Selective_School === 'Yes' && ' (selective - no zone)'}
          </h3>
          <p className="text-gray-500">{school.properties.Suburb}</p>
        </div>
        <div className={`text-lg ${getRankColorClass(school.properties.Rank)}`}>
          Rank: {school.properties.Rank || 'N/A'}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div>
          <p className="text-sm text-gray-600">Education Sector</p>
          <p>{school.properties.Education_Sector || 'N/A'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Selective School</p>
          <p>{school.properties.Selective_School || 'N/A'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">{selectedSchoolType === 'Primary' ? 'Naplan Score' : 'VCE Score'}</p>
          <p className={getRankColorClass(school.properties.Rank)}>
            {selectedSchoolType === 'Primary' 
              ? (school.properties.Naplan_Score || 'N/A')
              : (school.properties.VCE_Score || 'N/A')}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Property Prices</p>
          <p>House: {formatPrice(school.properties.median_house_price)}</p>
          <p>Unit: {formatPrice(school.properties.median_unit_price)}</p>
        </div>
      </div>
    </div>
  );

  // Compact table with responsive columns
  const CompactTable = () => (
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer w-16" onClick={() => requestSort('Rank')}>
            Rank {getSortIndicator('Rank')}
          </th>
          <th className="px-2 py-3 text-left text-xs font-semibold text-gray-700 flex-grow uppercase tracking-wider cursor-pointer" onClick={() => requestSort('School_Name')}>
            School {getSortIndicator('School_Name')}
          </th>
          <th className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer w-24" onClick={() => requestSort(selectedSchoolType === 'Primary' ? 'Naplan' : 'VCE_Score')}>
            {selectedSchoolType === 'Primary' ? 'Naplan' : 'VCE'} {getSortIndicator(selectedSchoolType === 'Primary' ? 'Naplan' : 'VCE_Score')}
          </th>
          <th className="px-2 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer w-24" onClick={() => requestSort('median_house_price')}>
            House $ {getSortIndicator('median_house_price')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {sortedSchools.map((school, index) => (
          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            <td className={`px-2 py-3 text-sm ${getRankColorClass(school.properties.Rank)}`}>
              {school.properties.Rank || 'N/A'}
            </td>
            <td className="px-2 py-3 text-sm">
              <div>
                <p className="font-medium">{school.properties.School_Name || 'N/A'}
                {school.properties.Selective_School === 'Yes' && ' (selective - no zone)'}
                </p>
                <p className="text-xs text-gray-500">{school.properties.Suburb || 'N/A'}</p>
              </div>
            </td>
            <td className={`px-2 py-3 text-sm ${getRankColorClass(school.properties.Rank)}`}>
              {selectedSchoolType === 'Primary' 
                ? (school.properties.Naplan_Score || 'N/A')
                : (school.properties.VCE_Score || 'N/A')}
            </td>
            <td className="px-2 py-3 text-sm">
              <p className="text-xs">{formatPrice(school.properties.median_house_price).replace('$', '$')}</p>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Full table with all columns
  const FullTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer w-16" onClick={() => requestSort('Rank')}>
              Rank {getSortIndicator('Rank')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('School_Name')}>
              School Name {getSortIndicator('School_Name')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('Suburb')}>
              Suburb {getSortIndicator('Suburb')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('Education_Sector')}>
              Sector {getSortIndicator('Education_Sector')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('Selective_School')}>
              Selective {getSortIndicator('Selective_School')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort(selectedSchoolType === 'Primary' ? 'Naplan' : 'VCE_Score')}>
              {selectedSchoolType === 'Primary' ? 'Naplan' : 'VCE'} {getSortIndicator(selectedSchoolType === 'Primary' ? 'Naplan' : 'VCE_Score')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('median_house_price')}>
              House $ {getSortIndicator('median_house_price')}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('median_unit_price')}>
              Unit $ {getSortIndicator('median_unit_price')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sortedSchools.map((school, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className={`px-3 py-3 text-sm ${getRankColorClass(school.properties.Rank)}`}>
                {school.properties.Rank || 'N/A'}
              </td>
              <td className="px-3 py-3 text-sm font-medium">
                {school.properties.School_Name || 'N/A'}
                {school.properties.Selective_School === 'Yes' && ' (selective - no zone)'}
              </td>
              <td className="px-3 py-3 text-sm">
                {school.properties.Suburb || 'N/A'}
              </td>
              <td className="px-3 py-3 text-sm">
                {school.properties.Education_Sector || 'N/A'}
              </td>
              <td className="px-3 py-3 text-sm">
                {school.properties.Selective_School || 'N/A'}
              </td>
              <td className={`px-3 py-3 text-sm ${getRankColorClass(school.properties.Rank)}`}>
                {selectedSchoolType === 'Primary' 
                  ? (school.properties.Naplan_Score || 'N/A')
                  : (school.properties.VCE_Score || 'N/A')}
              </td>
              <td className="px-3 py-3 text-sm">
                {formatPrice(school.properties.median_house_price)}
              </td>
              <td className="px-3 py-3 text-sm">
                {formatPrice(school.properties.median_unit_price)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Mobile Card View
  const CardView = () => (
    <div className="space-y-4">
      {sortedSchools.map((school, index) => (
        <SchoolCard key={index} school={school} />
      ))}
    </div>
  );

  return (
    <div className="h-screen flex flex-col">
      <Header
        activeTab="rankings"
        selectedSchoolType={selectedSchoolType.toLowerCase()}
        onSupportClick={onSupportClick}
      />
      
      <div className="flex-1 overflow-auto p-4 min-h-0">
        <div className="max-w-7xl mx-auto">
          {/* Controls Section */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
              {/* School Type Toggle */}
              <div className="flex items-center space-x-4">
                <h3 className="font-medium">School Type:</h3>
                <fieldset className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="SchoolType"
                      value="Primary"
                      checked={selectedSchoolType === "Primary"}
                      onChange={() => setSelectedSchoolType("Primary")}
                      className="mr-2"
                    /> 
                    <span className="text-sm text-gray-700">Primary</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="SchoolType"
                      value="Secondary"
                      checked={selectedSchoolType === "Secondary"}
                      onChange={() => setSelectedSchoolType("Secondary")}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Secondary</span>
                  </label>
                </fieldset>
              </div>
              
              {/* Search Box */}
              <div className="w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search By School Or Suburb..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Education Sector Filter */}
            <div className="mt-4">
              <h3 className="font-medium mb-2">Education Sector:</h3>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="educationSector"
                    value="All"
                    checked={educationSector === "All"}
                    onChange={() => setEducationSector("All")}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">All</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="educationSector"
                    value="Government"
                    checked={educationSector === "Government"}
                    onChange={() => setEducationSector("Government")}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Government</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="educationSector"
                    value="Catholic"
                    checked={educationSector === "Catholic"}
                    onChange={() => setEducationSector("Catholic")}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Catholic</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="educationSector"
                    value="Independent"
                    checked={educationSector === "Independent"}
                    onChange={() => setEducationSector("Independent")}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Independent</span>
                </label>
              </div>
            </div>
            
            {/* View Type Toggle and Results Count */}
            <div className="flex justify-between items-center mt-4">
              <p className="text-sm text-gray-600">
                {sortedSchools.length} Schools Found
              </p>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">View:</span>
                <div className="flex border rounded overflow-hidden">
                  <button 
                    className={`px-3 py-1 text-sm ${viewMode === 'card' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                    onClick={() => setViewMode('card')}
                    aria-label="Card view"
                  >
                    Cards
                  </button>
                  <button 
                    className={`px-3 py-1 text-sm ${viewMode === 'compact' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                    onClick={() => setViewMode('compact')}
                    aria-label="Compact table view"
                  >
                    Compact
                  </button>
                  <button 
                    className={`px-3 py-1 text-sm ${viewMode === 'full' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                    onClick={() => setViewMode('full')}
                    aria-label="Full table view"
                  >
                    Full
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Table/Card Section */}
          {loading ? (
            <div className="text-center py-10">
              <p className="text-lg">Loading School Data...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {viewMode === 'card' && <CardView />}
              {viewMode === 'compact' && <CompactTable />}
              {viewMode === 'full' && <FullTable />}
              
              {sortedSchools.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-gray-500">No schools found matching your search criteria.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SchoolListView;